// Shared "which date matters, and how late is it" rules for clinical records.
//
// Lifted out of `src/components/admin/home/NotificationsPanel.jsx` (TGCLOUD-1670)
// so the home Overdue panel and the pedigree data overlays cannot drift apart on
// what "overdue" means. The panel imports these back; its behaviour is unchanged
// except for RESOLVED_APPOINTMENT_STATUSES below.
//
// One deliberate change came with the lift. `isResolvedAppointment` used to test
// `appointmentStatus === 'Closed'` alone. The forms only ever write Open/Closed
// (AppointmentForm.jsx:393), but imports, the Calendly webhook and the demo seeds
// also write 'Attended' and 'Scheduled' (05-clinical.js:115, 08-pathways.js:333),
// and a status is missing altogether on pathway-created appointments
// (pathway.js:551-560). Treating 'Attended' and 'Cancelled' as unresolved made
// every attended appointment render as a red "Past due" row. They are resolved.
//
// A MISSING status is deliberately NOT resolved: an appointment nobody has closed
// out is exactly what an outstanding-appointment overlay is looking for.

import { differenceInCalendarDays } from 'date-fns';

/**
 * Appointment statuses that mean "dealt with, stop chasing it".
 *
 * Compared case-insensitively after trimming, because the field is a bare String
 * with no schema enum (appointment-model.js:40-42), imports accept any string and
 * an unvalidated PUT can write anything (clinical-note.js:527-541).
 */
export const RESOLVED_APPOINTMENT_STATUSES = Object.freeze([
  'Closed',
  'Attended',
  'Cancelled'
]);

const RESOLVED_SET = new Set(
  RESOLVED_APPOINTMENT_STATUSES.map((s) => s.toLowerCase())
);

/** Case-insensitive membership test tolerant of a missing or padded value. */
export const isResolvedAppointmentStatus = (value) =>
  value != null && RESOLVED_SET.has(String(value).trim().toLowerCase());

/**
 * The date that decides whether a record is due, per record type.
 *
 * `n.type` is the notification-panel shape; `n.noteType` is the stored
 * discriminator. Both are accepted so the overlay evaluator can pass a raw note.
 */
export function getRelevantDate(n) {
  if (!n) return null;
  switch (n.type || n.noteType) {
    case 'Appointment':
      return n.appointmentDate;
    case 'Test':
      return n.reminderDate || n.dateResultDue;
    case 'Referral':
      return n.reminderDate;
    case 'Contact':
      return n.reminderDate || n.contactDate;
    case 'Clinical Note':
      return n.reminderDate || n.clinicalNoteDate;
    case 'Sample':
      return n.reminderDate || n.dateTaken;
    case 'Consent':
      return n.reminderDate || n.consentDate;
    default:
      return null;
  }
}

/** Anything older than this drops out of the panel's Overdue section entirely. */
export const PAST_CAP_DAYS = 90;
/** How far ahead the panel's "Coming" section looks. */
export const FUTURE_WINDOW_DAYS = 7;

export function isResolvedAppointment(n) {
  return (
    (n?.type || n?.noteType) === 'Appointment' &&
    isResolvedAppointmentStatus(n?.appointmentStatus)
  );
}

/**
 * Severity bands, shared by the panel's badges and the overlay's ring weight.
 * `days` is days PAST due (positive = late).
 */
export const DUE_BANDS = Object.freeze([
  { minDays: 21, key: 'over2w', label: 'Past due > 2 weeks', bg: '#e05252' },
  { minDays: 14, key: 'twoWeeks', label: 'Past due 2 weeks', bg: '#e0602b' },
  { minDays: 7, key: 'oneWeek', label: 'Past due 1 week', bg: '#e0822b' },
  { minDays: 1, key: 'due', label: 'Past due', bg: '#e0a23b' }
]);

/**
 * Band for a number of days past due. Returns null when not yet late.
 * Used by the overlay to pick a ring stroke weight, and by the panel for badges.
 */
export function dueBand(days) {
  if (!Number.isFinite(days) || days < 1) return null;
  return DUE_BANDS.find((b) => days >= b.minDays) || null;
}

/**
 * Bucket a notification into overdue / today / coming, or null when it is out of
 * both windows. Byte-for-byte the panel's previous behaviour apart from the two
 * extra resolved statuses.
 */
export function getBucket(n, now = new Date()) {
  const date = getRelevantDate(n);
  if (!date) return { section: 'today', badge: null, days: null };

  const days = differenceInCalendarDays(now, new Date(date));

  if (days > PAST_CAP_DAYS) return null;
  if (days > 0) {
    if (isResolvedAppointment(n)) {
      return {
        section: 'overdue',
        badge: { label: 'Closed', bg: '#9aa3af' },
        days
      };
    }
    const band = dueBand(days);
    return {
      section: 'overdue',
      badge: { label: band.label, bg: band.bg },
      days
    };
  }
  if (days === 0) {
    return { section: 'today', badge: { label: 'Due', bg: '#3b9ae0' }, days };
  }
  if (days >= -FUTURE_WINDOW_DAYS) {
    return { section: 'coming', badge: null, days };
  }
  return null;
}
